import { AppointmentResponse, UpdateTaskRequest } from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import { dateLocalString } from "src/lib/date";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { TrackType } from "src/state/Track/TrackCubit";
import { toast, tracker } from "src/state/state";
import {
  AppPopup,
  AppQueryPopupsController
} from "../AppQueryPopups/AppQueryPopupsBloc";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";
import BlockingLoadingOverlayController from "../BlockingLoadingOverlay/BlockingLoadingOverlayController";
import LabStatusOverviewCubit from "../LabStatusOverview/LabStatusOverviewCubit";

export type BookingConfirmationOptions = Parameters<
  typeof BlockingLoadingOverlayController.loadingSuccess
>[0];

const SKIP_CONFIRMATION_EVENT = "Skip Coaching Session Confirmation";

type AppointmentSchedulerState = {
  isSkipConfirmationOpen: boolean;
};

export default class AppointmentSchedulerCubit extends Cubit<AppointmentSchedulerState> {
  constructor() {
    super({ isSkipConfirmationOpen: false });
  }

  openSkipConfirmation = (taskPath: string): void => {
    this.patch({ isSkipConfirmationOpen: true });
    tracker.track(SKIP_CONFIRMATION_EVENT, {
      type: TrackType.open,
      data: { taskPath }
    });
  };

  closeSkipConfirmation = (): void => {
    this.patch({ isSkipConfirmationOpen: false });
  };

  /** Dismissal by the member, as opposed to `closeSkipConfirmation`, which also runs on unmount and must stay silent. */
  cancelSkipConfirmation = (taskPath: string): void => {
    this.closeSkipConfirmation();
    tracker.track(SKIP_CONFIRMATION_EVENT, {
      type: TrackType.cancel,
      data: { taskPath }
    });
  };

  /**
   * Marks the task the scheduler was opened for as skipped and closes the
   * scheduler. `taskPath` is the `program/group/slug` triple carried in the
   * popup's query params, since the scheduler is reached by URL rather than
   * by props. `updateTaskStatus` patches state optimistically, so a falsy
   * result means the PATCH failed or the transition was rejected — we leave
   * the member in the scheduler to retry rather than closing on a lie.
   */
  skipTask = async (taskPath: string): Promise<void> => {
    const [program, group, slug] = taskPath.split("/");

    if (!program || !group || !slug) {
      reportErrorSentry(new Error(`Malformed skip task path: ${taskPath}`));
      return;
    }

    this.closeSkipConfirmation();

    const updatedTask = await Blac.getBloc(TaskManagementBloc).updateTaskStatus(
      { program, group, slug },
      UpdateTaskRequest.status.SKIPPED
    );

    if (!updatedTask) {
      toast.error("error.generic.short.tryAgain");
      return;
    }

    tracker.track(SKIP_CONFIRMATION_EVENT, {
      type: TrackType.complete,
      data: { taskPath }
    });
    AppQueryPopupsController.closePopup();
  };

  /**
   * Build the confirmation overlay options for a freshly booked appointment.
   * When the member still has incomplete required labs we nudge them to get
   * their records ready (the Care Team relies on up-to-date results); otherwise
   * we show a plain confirmation that dismisses itself. Labs that are already
   * in progress (requisition issued or an upload under review) count as handled,
   * so we skip the nudge even though the fulfillment status is still incomplete.
   */
  resolveBookingConfirmationOptions = async (
    appointment: AppointmentResponse
  ): Promise<BookingConfirmationOptions> => {
    const title = translate("appointment.confirmation.title", {
      dateTime: dateLocalString(appointment.start)
    });

    const labStatus = Blac.getBloc(LabStatusOverviewCubit);
    await labStatus.fetchLabStatus(true);

    const { hasInitialLabsInProgress } = Blac.getBloc(TaskManagementBloc);

    if (labStatus.hasIncompleteRequiredLabs && !hasInitialLabsInProgress) {
      return {
        title,
        message: "appointment.confirmation.labs.incomplete.description",
        showIcon: false,
        primaryAction: {
          text: "appointment.confirmation.labs.cta",
          onClick: () => AppQueryPopupsController.openPopup(AppPopup.initialLab)
        },
        secondaryAction: {
          text: "notNow",
          onClick: () => undefined
        }
      };
    }

    return {
      title,
      autoDismiss: true,
      primaryAction: {
        text: "close",
        onClick: () => undefined
      }
    };
  };

  showBookingConfirmation = (options: BookingConfirmationOptions): void => {
    BlockingLoadingOverlayController.loadingSuccess(options);
  };
}
